import React, { useEffect, useState, useMemo } from "react";
import { EditableText } from "@digital-twins/deck-ui/slides/EditableText";
import { useTranslation } from "react-i18next";
import { FadeImage } from "@/components/ui/FadeImage";
import { AlertCircle, Loader2 } from "@digital-twins/design-system/icons";
import { DeckStatus, ReportAvailability } from "@digital-twins/shared-types/reporting";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import { ContentLanguageProvider } from "@/i18n/contentLanguageContext";
import { cn } from "@/lib/utils";
import { audienceDisplayName } from "@/lib/audience-display-name";
import { ReportHeader } from "./views/overview/ReportHeader";
import { StudyTabBar, type StudyTab } from "./StudyTabBar";
import { SummaryTab } from "./tabs/SummaryTab";
import { DeckTab } from "./tabs/DeckTab";
import { ChatTab } from "./tabs/ChatTab";
import { AnalystTab, type AnalystTabMode } from "./tabs/AnalystTab/AnalystTab";
import { SurveyDataTab } from "./tabs/SurveyDataTab";
import { SurveyFiguresProvider } from "@/features/studies/report/figures/SurveyFiguresProvider";
import { StudyTypePill } from "./hero/StudyTypePill";
import { TestedAssetStack } from "./hero/TestedAssetStack";
import {
  AudienceIcon,
  eyebrowIconClass,
  eyebrowItemClass,
  FilteredIcon,
} from "./hero/eyebrowMeta";
import { TestedAssetPreviewDialog } from "./hero/TestedAssetPreviewDialog";
import { TestedAssetSlider } from "./hero/TestedAssetSlider";
import {
  previewedAssets,
  readTestedAssets,
} from "@/features/studies/report/utils/testedAssets";
import { withLegacySummaryCards } from "@/features/studies/report/utils/legacySummaryCards";
import {
  interpretationNotice,
  reportFailureScope,
  savedAudienceUnavailable,
} from "@/features/studies/report/readiness";
import {
  failedExhibitsOf,
  reportViewState,
  storylineViewState,
} from "@/features/studies/report/reportViewState";
import {
  parseStoryline,
  storylineSlideNumbersFor,
} from "@/features/studies/report/storyline";
import { visibleFailedExhibits } from "./deck/deckPager";
import { resolveIncompleteTwinDelivery } from "@/features/studies/incompleteTwinDelivery";
import { IncompleteTwinDeliveryNotice } from "./IncompleteTwinDeliveryNotice";
import { isKnownStudyGoal } from "@/features/studies/studyTypeMeta";
import { getAllQuestions } from "@/lib/utils/chart-utils";
import type { Deck } from "@digital-twins/deck-ui/slides/contracts";
import type { CanonicalAnalysisOutput } from "@digital-twins/shared-types";
import type { PublicStoryline } from "@digital-twins/shared-types/reporting";
import type { SegmentationMapping } from "@/types/components/audiences/segmentation-mapping";
import type { PopulationFilter } from "@/types/routes/test-results";
import { useBackofficeSummaryEdits } from "../useBackofficeSummaryEdits";
import {
  isCornerDenseStudyTab,
  isDeckLaneStudyTab,
  useFeedbackCornerOccupied,
  useFeedbackDeckLane,
} from "@/lib/feedback";


const NO_SLIDE_NUMBERS: ReadonlyMap<string, number> = new Map();

export interface PublicReportIntentReplay {
  id: number;
  tab: Exclude<StudyTab, "summary">;
}

function ReportTabContent({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "mx-auto w-full max-w-[1400px] flex-1 min-h-0 flex flex-col px-4 md:px-8",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface ExecutiveSummary {
  headline?: string;
  narrative?: string;
  featuredQuotes?: Array<{
    text?: string;
    fullText?: string;
    displayText?: string;
    excerpt?: string;
    attribution?: string;
    name?: string;
    imageUrl?: string;
    role?: string;
    /** Survey question the quote answers, resolved server-side. Absent on older analyses. */
    questionLabel?: string;
    sentiment?: "positive" | "negative" | "neutral";
  }>;
  /**
   * Name/role/portrait for every respondent the qualitative analysis quotes, not
   * just the featured subset above. The Survey Data tab's open-end theme cards
   * join their theme quotes to this by respondentId/respondentLabel.
   */
  quoteRespondentProfiles?: Array<{
    respondentId?: string;
    respondentLabel?: string;
    name?: string;
    role?: string;
    imageUrl?: string;
  }>;
  metricCards?: Array<{
    title: string;
    type: "circular" | "comparison";
    primaryValue?: number;
    primaryLabel?: string;
    valueType?: "percentage" | "average";
    scale?: string;
    comparisons?: Array<{
      label: string;
      value: number;
      valueType?: "percentage" | "average";
    }>;
  }>;
}

export interface ReportLandingPageProps {
  studyId: string;
  studyName: string;
  createdAt?: Date | string;
  survey: any;
  responses: any[];
  populationFilter?: PopulationFilter | string;
  appliedSegmentNames?: Array<string>;
  executiveSummary?: ExecutiveSummary;
  canonicalAnalysis?: CanonicalAnalysisOutput | null;
  /** The storyline every reader receives (owner, teammate, share link); parsed here. */
  storyline?: PublicStoryline | null;
  audienceProfiles?: any[];
  audienceProfilesLoading?: boolean;
  coverImageUrl?: string;
  reportHeadline?: string;
  userId?: string;
  organizationToken?: string;
  storageName?: string;
  populationId?: string;
  populationName?: string;
  populationCoverUrl?: string;
  analysisColumns?: string[];
  segmentationMappings?: SegmentationMapping[] | null;
  constantContext?: Record<string, string> | null;
  deck?: Deck | null;
  isDeckLoading?: boolean;
  /** The served deck status (`servedDeckStatus`); drives the deck view state. */
  deckStatus?: DeckStatus | null;
  /** Delivery format: false → study launched without a deck; deck tab is hidden. */
  deckEnabled?: boolean;
  /** Delivery format: false → twin chat tab is hidden (frontend-only consumption). */
  twinChatEnabled?: boolean;
  /** Owner-only capability returned by the authenticated full-report response. */
  canUseReportAnalyst?: boolean;
  /** The current analysis has its scale tab book (SS-6): the download menu offers "Tables". */
  hasScaleTables?: boolean;
  studyWorkbookStatus?: 'legacy' | 'building' | 'ready' | 'failed';
  /** Public share permission: guests see no chat UI unless the owner enabled it. */
  guestChatEnabled?: boolean;
  onLoadDeck?: () => Promise<boolean>;
  onDeckUpdate?: (deck: Deck) => void;
  isRegeneratingSummary?: boolean;
  onRegenerateSummary?: () => void;
  /** The caller may manage sharing (server `canManage`); hides Share study otherwise. */
  canShare?: boolean;
  reportStatus?: ReportAvailability["reportStatus"];
  /** Accepted section state (readiness.availability); drives the unfinished-interpretation notice. */
  availability?: ReportAvailability | null;
  isPublicView?: boolean;
  shareToken?: string;
  isShared?: boolean;
  onSharingChanged?: () => void;
  brandLogoUrl?: string | null;
  brandLogoUrlOnDark?: string | null;
  experimentLanguage?: string | null;
  onPublicInteractionIntent?: (tab: Exclude<StudyTab, "summary">) => boolean;
  publicIntentReplay?: PublicReportIntentReplay | null;
  /** Study method (lobby goal id). Drives the hero's type pill; no pill for null/unknown. */
  goalId?: string | null;
  /** Results-file metadata. The outcome ledger lives here when the run recorded one. */
  resultsMetadata?: unknown;
  respondentsCompleted?: number;
  numRespondents?: number;
  /** Study lifecycle status. The shortfall notice is only for a completed run. */
  studyStatus?: string | null;
}

export function isStudyChatHidden(input: {
  twinChatEnabled: boolean
  isPublicView: boolean
  guestChatEnabled: boolean
}): boolean {
  return (
    !input.twinChatEnabled ||
    (input.isPublicView && !input.guestChatEnabled)
  )
}

export function ReportLandingPage({
  studyId,
  studyName,
  createdAt,
  survey,
  responses,
  populationFilter,
  appliedSegmentNames,
  executiveSummary,
  canonicalAnalysis,
  storyline: rawStoryline = null,
  audienceProfiles,
  audienceProfilesLoading,
  coverImageUrl,
  reportHeadline,
  userId,
  organizationToken,
  populationId,
  populationName,
  analysisColumns = [],
  segmentationMappings = null,
  constantContext = null,
  deck,
  isDeckLoading = false,
  deckStatus = null,
  deckEnabled = true,
  twinChatEnabled = true,
  canUseReportAnalyst = false,
  hasScaleTables = false,
  studyWorkbookStatus = 'legacy',
  guestChatEnabled = false,
  onLoadDeck,
  onDeckUpdate,
  isRegeneratingSummary = false,
  onRegenerateSummary,
  canShare = false,
  reportStatus = "ready",
  availability = null,
  isPublicView = false,
  shareToken,
  isShared = false,
  onSharingChanged,
  brandLogoUrl,
  brandLogoUrlOnDark,
  experimentLanguage,
  onPublicInteractionIntent,
  publicIntentReplay,
  goalId,
  resultsMetadata,
  respondentsCompleted,
  numRespondents,
  studyStatus,
}: ReportLandingPageProps) {
  const { t } = useTranslation("app");
  const audienceName = audienceDisplayName(populationName);
  const [activeTab, setActiveTab] = useState<StudyTab>("summary");
  // The sequence item a storyline exhibit link opens the Deck tab at.
  const [deckFocusSlideId, setDeckFocusSlideId] = useState<string | null>(null);
  // Survey data, twin chat, and the analyst composer fill the corner the
  // feedback toast uses. Hold auto feedback until the user is on summary or deck.
  useFeedbackCornerOccupied(isCornerDenseStudyTab(activeTab));
  // Deck pins export and navigation along the bottom edge; lane feedback above it.
  useFeedbackDeckLane(isDeckLaneStudyTab(activeTab));
  // Readable results with an unfinished interpretation; the
  // accepted publication outranks the legacy status column.
  const notice = interpretationNotice(availability);
  const twinShortfall = resolveIncompleteTwinDelivery({
    metadata: resultsMetadata,
    completed: respondentsCompleted,
    requested: numRespondents,
    status: studyStatus,
  });
  const bannerFailed =
    notice === "failed" || (notice === null && reportStatus === "failed");
  // A failed exhibit is told on its own slide in the deck pager, never
  // by the page banner; section-level failures keep the banner.
  const isExhibitOnlyFailure =
    notice === null &&
    reportStatus === "failed" &&
    reportFailureScope(availability) === "exhibits";
  const showStatusBanner =
    (notice !== null || reportStatus !== "ready") && !isExhibitOnlyFailure;
  // The analysis was built without its saved audience segments: a limitation,
  // not a failure. Only the owner can regenerate, so only the owner is told how.
  const showSavedAudienceNotice = savedAudienceUnavailable(canonicalAnalysis);
  const {
    canEdit: canEditContent,
    summary: editedSummary,
    applyPatch: applySummaryPatch,
    save: saveSummary,
    isDirty: isSummaryDirty,
    isSaving: isSummarySaving,
  } = useBackofficeSummaryEdits(
    studyId,
    (executiveSummary ?? {}) as Record<string, unknown>,
  );
  const liveSummary = (editedSummary ?? executiveSummary) as
    | ExecutiveSummary
    | undefined;
  const [initialChatRespondentId, setInitialChatRespondentId] = useState<
    string | undefined
  >();
  const deckViewState = reportViewState(availability, {
    status: deckStatus,
    hasSlides: Array.isArray(deck?.slides) && deck.slides.length > 0,
    isFetching: isDeckLoading,
  });
  const isDeckFailed = deckViewState.kind === "deck_failed";
  // The accepted sequence places a failed exhibit in the deck pager.
  const slideOrder = useMemo(
    () =>
      (canonicalAnalysis?.effectiveSequence?.items ?? []).map((item) => item.id),
    [canonicalAnalysis?.effectiveSequence],
  );
  const isDeckHidden = !deckEnabled;
  // The reader storyline, parsed once at the page boundary against its
  // generated contract; a rejected payload is a typed malformed state, never
  // a throw.
  const parsedStoryline = useMemo(
    () => (rawStoryline == null ? null : parseStoryline(rawStoryline)),
    [rawStoryline],
  );
  const storyline = storylineViewState(availability, deckViewState, parsedStoryline);
  // Slide links number a slide as the viewer's pager does (visible index + 1).
  const storylineSlideNumbers = useMemo(
    () =>
      parsedStoryline?.ok && deckViewState.kind === "deck_ready" && deck
        ? storylineSlideNumbersFor(parsedStoryline.data, deck, {
            failedExhibits: visibleFailedExhibits(failedExhibitsOf(availability), { isPublicView }),
            slideOrder,
          })
        : NO_SLIDE_NUMBERS,
    [parsedStoryline, deckViewState.kind, deck, availability, isPublicView, slideOrder],
  );
  const assets = useMemo(() => readTestedAssets(survey), [survey]);
  const itemTags = canonicalAnalysis?.itemTags ?? {};
  const heroSlides = useMemo(
    () => previewedAssets(assets, canonicalAnalysis?.itemTags ?? {}, canonicalAnalysis?.assetPreviews),
    [assets, canonicalAnalysis?.itemTags, canonicalAnalysis?.assetPreviews],
  );
  // Studies analysed before `summaryCards` existed draw what their stored
  // executive summary featured instead of two empty frames.
  const summaryAnalysis = useMemo(
    () => withLegacySummaryCards(canonicalAnalysis, executiveSummary),
    [canonicalAnalysis, executiveSummary],
  );
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);

  useEffect(() => {
    if ((isDeckHidden || isDeckFailed) && activeTab === "deck") {
      setActiveTab("summary");
    }
  }, [isDeckHidden, isDeckFailed, activeTab]);

  const isChatHidden = isStudyChatHidden({
    twinChatEnabled,
    isPublicView,
    guestChatEnabled,
  });
  // The owner chats with the analyst; teammates and share links read the
  // storyline alone, and see the tab only once it is ready.
  const analystMode: AnalystTabMode =
    !isPublicView && canUseReportAnalyst ? "owner" : "readOnly";
  const isAnalystHidden =
    analystMode === "readOnly" && storyline.kind !== "storyline_ready";
  const hiddenTabs = useMemo<Array<StudyTab>>(
    () => [
      ...(isDeckHidden ? (["deck"] as const) : []),
      ...(isChatHidden ? (["chat"] as const) : []),
      ...(isAnalystHidden ? (["analyst"] as const) : []),
    ],
    [isDeckHidden, isChatHidden, isAnalystHidden],
  );
  const disabledTabs = useMemo<Array<StudyTab>>(
    () => (isDeckFailed && !isDeckHidden ? (["deck"] as const) : []),
    [isDeckFailed, isDeckHidden],
  );
  const disabledTabTooltips = useMemo<Partial<Record<StudyTab, string>>>(
    () =>
      isDeckFailed && !isDeckHidden
        ? { deck: t("studyReport.deckCreationFailedTooltip") }
        : {},
    [isDeckFailed, isDeckHidden, t],
  );

  const deckStory = deck?.metadata?.storyManifest;

  const headline =
    liveSummary?.headline ||
    executiveSummary?.headline ||
    deckStory?.headline ||
    reportHeadline;

  const narrativeText: string | undefined =
    liveSummary?.narrative ||
    executiveSummary?.narrative ||
    deckStory?.narrative;

  const questionCount = useMemo(() => {
    if (!survey) return 0;
    try {
      return getAllQuestions(survey).length;
    } catch {
      return 0;
    }
  }, [survey]);

  const displayHeadline =
    headline || (studyName ? `${studyName} Results` : "Survey Results");

  const { segmentTags, filterDescriptions } = useMemo(() => {
    const explicit =
      appliedSegmentNames ??
      canonicalAnalysis?.appliedSegmentNames ??
      undefined;

    const tags: Array<string> = explicit && explicit.length > 0 ? [...explicit] : [];

    if (!populationFilter) return { segmentTags: tags.length > 0 ? tags : undefined, filterDescriptions: undefined };
    const filter =
      typeof populationFilter === "string"
        ? (() => { try { return JSON.parse(populationFilter); } catch { return null; } })()
        : populationFilter;
    if (!filter?.conditions) return { segmentTags: tags.length > 0 ? tags : undefined, filterDescriptions: undefined };

    function humanizeField(raw: string): string {
      let cleaned = raw.replace(/_/g, " ");
      cleaned = cleaned.replace(/^recod\s+/i, "");
      return cleaned.replace(/\b\w/g, (c: string) => c.toUpperCase()).trim();
    }

    function describeCondition(field: string, value: unknown): string | null {
      if (!field || !value) return null;
      const label = humanizeField(field);
      const val = Array.isArray(value) ? value.join(", ") : String(value);
      return `${label}: ${val}`;
    }

    const labels: Array<string> = [];
    for (const cond of filter.conditions) {
      const field = cond.field || cond.sourceColumn;
      if (field && cond.value) {
        const desc = describeCondition(field, cond.value);
        if (desc) labels.push(desc);
      } else if (cond.conditions) {
        for (const nested of cond.conditions) {
          const desc = describeCondition(nested.field || "", nested.value);
          if (desc) labels.push(desc);
        }
      }
    }
    return {
      segmentTags: tags.length > 0 ? tags : undefined,
      filterDescriptions: labels.length > 0 ? labels : undefined,
    };
  }, [appliedSegmentNames, canonicalAnalysis?.appliedSegmentNames, populationFilter]);

  const handleTabChange = (tab: StudyTab) => {
    if (
      tab !== "summary" &&
      onPublicInteractionIntent &&
      !onPublicInteractionIntent(tab)
    ) {
      return;
    }
    setActiveTab(tab);
    setDeckFocusSlideId(null);
    if (tab !== "chat") setInitialChatRespondentId(undefined);
  };

  const handleOpenStorylineSlide = (slideId: string) => {
    handleTabChange("deck");
    setDeckFocusSlideId(slideId);
  };

  useEffect(() => {
    if (!publicIntentReplay) return;
    setActiveTab(publicIntentReplay.tab);
    if (publicIntentReplay.tab !== "chat") {
      setInitialChatRespondentId(undefined);
    }
  }, [publicIntentReplay]);

  useEffect(() => {
    if (hiddenTabs.includes(activeTab)) {
      setActiveTab("summary");
    }
  }, [activeTab, hiddenTabs]);

  const handleTwinChatClick = (respondentId: string) => {
    if (isChatHidden) return;
    if (onPublicInteractionIntent && !onPublicInteractionIntent("chat")) {
      return;
    }
    setInitialChatRespondentId(respondentId);
    setActiveTab("chat");
  };
  const _handleSurveyData = () => setActiveTab("survey");

  // The deck's generation time is the report revision the page shows (as in
  // DeckViewer); a new one re-reads the figure service's list of cuts.
  const figuresRevisionHint = deck?.createdAt ? String(deck.createdAt) : undefined;

  return (
    <ContentLanguageProvider language={experimentLanguage}>
    {/* One figure source per report page (plots W6-R): the summary's question
        cards and the survey tab draw the figure service's plots, owner by
        study id, share link by token; the list of cuts is read only once a
        card asks. The summary falls back to today's charts when the service
        cannot draw a card; the survey tab opts back into its own states. */}
    <SurveyFiguresProvider
      studyId={isPublicView ? undefined : studyId}
      shareToken={isPublicView ? shareToken : undefined}
      metaLoading="on-demand"
      failurePolicy="legacy"
      revisionHint={figuresRevisionHint}
    >
    <div
      className={cn(
        "relative flex flex-col bg-[hsl(var(--report-bg))]",
        isPublicView
          ? "min-h-full"
          : "h-[calc(100dvh-var(--feedback-manual-lane))] overflow-hidden",
      )}
    >
      {/* ── Sticky top bar ── */}
      {/* Same 1400px cap + centering as ReportTabContent, so the header stays
          aligned with the tab content it sits above on wide monitors. */}
      <div className="mx-auto w-full max-w-[1400px] shrink-0 px-4 md:px-8 py-4 z-10">
        <ReportHeader
          respondentCount={responses?.length}
          questionCount={questionCount}
          date={createdAt}
          isShared={isPublicView || isShared}
          experimentId={isPublicView ? undefined : studyId}
          studyName={studyName}
          coverImageUrl={coverImageUrl}
          isPublicView={isPublicView}
          organizationToken={organizationToken}
          userId={userId}
          onSharingChanged={onSharingChanged}
          canShare={canShare}
          brandLogoUrl={brandLogoUrl}
          brandLogoUrlOnDark={brandLogoUrlOnDark}
          centerSlot={
            activeTab !== "summary" ? (
              <StudyTabBar
                activeTab={activeTab}
                onTabChange={handleTabChange}
                hiddenTabs={hiddenTabs}
                disabledTabs={disabledTabs}
                disabledTabTooltips={disabledTabTooltips}
              />
            ) : undefined
          }
        />

        {/* Report status banner */}
        {showStatusBanner && (
          <div
            data-testid="report-status-banner"
            data-interpretation={notice ?? undefined}
            className={
              bannerFailed
                ? "mt-3 flex items-start justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3"
                : "mt-3 flex items-start justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3"
            }
          >
            <div className="flex items-start gap-2.5">
              <AlertCircle
                className={
                  bannerFailed
                    ? "h-4 w-4 text-destructive mt-0.5"
                    : "h-4 w-4 text-primary mt-0.5"
                }
              />
              <div className="space-y-1">
                <Text
                  size="sm"
                  className="font-medium text-[hsl(var(--report-heading))]"
                >
                  {notice === "pending"
                    ? t("studyReport.interpretationPending")
                    : notice === "failed"
                      ? t("studyReport.interpretationFailed")
                      : reportStatus === "failed"
                        ? t("studyReport.reportAssetsFailed")
                        : t("studyReport.reportAssetsGenerating")}
                </Text>
                <Text size="xs" className="text-[hsl(var(--report-muted))]">
                  {notice === "pending"
                    ? t("studyReport.interpretationPendingHelp")
                    : notice === "failed"
                      ? t("studyReport.interpretationFailedHelp")
                      : reportStatus === "failed"
                        ? t("studyReport.reportAssetsFailedHelp")
                        : t("studyReport.reportAssetsGeneratingHelp")}
                </Text>
              </div>
            </div>
            {bannerFailed &&
              onRegenerateSummary &&
              !isPublicView && (
                <Button
                  data-testid="report-status-retry-button"
                  variant="primary"
                  size="sm"
                  className="text-sm"
                  onClick={onRegenerateSummary}
                  disabled={isRegeneratingSummary}
                >
                  {isRegeneratingSummary ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      {t("studyReport.retryingGeneration")}
                    </>
                  ) : (
                    t("studyReport.retryGeneration")
                  )}
                </Button>
              )}
          </div>
        )}

        {showSavedAudienceNotice && (
          <div
            data-testid="report-saved-audience-notice"
            className="mt-3 flex items-start gap-2.5 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3"
          >
            <AlertCircle className="h-4 w-4 text-primary mt-0.5" />
            <div className="space-y-1">
              <Text
                size="sm"
                className="font-medium text-[hsl(var(--report-heading))]"
              >
                {t("studyReport.savedAudienceUnavailable")}
              </Text>
              {!isPublicView && (
                <Text size="xs" className="text-[hsl(var(--report-muted))]">
                  {t("studyReport.savedAudienceUnavailableHelp")}
                </Text>
              )}
            </div>
          </div>
        )}
        {twinShortfall && (
          <IncompleteTwinDeliveryNotice delivery={twinShortfall} />
        )}
      </div>

      {/* ── Scrollable / tab content area ── */}
      <div
        className={cn(
          "relative flex-1 flex flex-col",
          isPublicView ? "min-h-[calc(100vh-120px)]" : "min-h-0 overflow-hidden",
        )}
      >
        {/* Single AnimatePresence for all tabs — absolute positioning prevents
            layout fighting between the entering and exiting tabs */}
        <AnimatePresence mode="wait">
          {activeTab === "summary" && (
            <motion.div
              key="summary-hero"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 overflow-y-auto scrollbar-overlay"
            >
              <ReportTabContent className="overflow-visible">
                {/* Hero: two separate cards with 16px gap; on a phone the
                    tested assets stack under the headline */}
                <div className="flex flex-col md:flex-row gap-4 min-h-[220px]">
                  {/* Left card — 2/3 width: headline + narrative + tab bar */}
                  <div className="md:flex-[2] rounded-2xl bg-[hsl(var(--report-card))] flex flex-col justify-between gap-4 px-5 py-6 md:px-8 md:py-8 min-w-0">
                    <div className="flex flex-col gap-3">
                      {(audienceName || isKnownStudyGoal(goalId) || assets.length > 0) && (
                        <div
                          className="flex flex-wrap items-center gap-x-4 gap-y-1.5"
                          data-testid="report-hero-meta"
                        >
                          {audienceName && (
                            <span className={eyebrowItemClass} data-testid="report-audience-name">
                              <AudienceIcon
                                className={eyebrowIconClass}
                                aria-hidden="true"
                                data-testid="report-audience-icon"
                                data-icon="users"
                              />
                              {audienceName}
                            </span>
                          )}
                          {(segmentTags || filterDescriptions) && (
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className={cn(eyebrowItemClass, "cursor-default")}>
                                    <FilteredIcon
                                      className={eyebrowIconClass}
                                      aria-hidden="true"
                                      data-icon="filter"
                                    />
                                    Filtered
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  align="start"
                                  className="max-w-xs space-y-1.5 px-3 py-2.5"
                                >
                                  {segmentTags && segmentTags.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                      {segmentTags.map((name) => (
                                        <span
                                          key={name}
                                          className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300"
                                        >
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {filterDescriptions?.map((desc) => (
                                    <div key={desc} className="text-xs text-muted-foreground">
                                      {desc}
                                    </div>
                                  ))}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          <StudyTypePill goalId={goalId} />
                          <TestedAssetStack
                            assets={assets}
                            itemTags={itemTags}
                            onOpen={setOpenAssetId}
                          />
                        </div>
                      )}
                      {canEditContent ? (
                        <EditableText
                          value={displayHeadline}
                          field="headline"
                          editable
                          multiline
                          tag="h1"
                          className="font-display text-[24px] md:text-[28px] leading-[1.2] tracking-tight text-[hsl(var(--report-heading))] pb-[0.1em]"
                          onEdit={(_field, value) =>
                            applySummaryPatch({ headline: value })
                          }
                        />
                      ) : (
                        <Heading
                          level={1}
                          className="font-display text-[24px] md:text-[28px] leading-[1.2] tracking-tight text-[hsl(var(--report-heading))] line-clamp-4 pb-[0.1em]"
                        >
                          {displayHeadline}
                        </Heading>
                      )}
                      {canEditContent ? (
                        <EditableText
                          value={narrativeText ?? ""}
                          field="narrative"
                          editable
                          multiline
                          tag="p"
                          className="text-sm leading-[1.6] text-[hsl(var(--report-muted))] min-h-[1.6em]"
                          onEdit={(_field, value) =>
                            applySummaryPatch({ narrative: value })
                          }
                        />
                      ) : (
                        narrativeText && (
                          <Text
                            size="sm"
                            className="leading-[1.6] text-[hsl(var(--report-muted))] line-clamp-3"
                          >
                            {narrativeText}
                          </Text>
                        )
                      )}
                    </div>

                    {/* Tab bar lives here on Summary */}
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <StudyTabBar
                        activeTab={activeTab}
                        onTabChange={handleTabChange}
                        hiddenTabs={hiddenTabs}
                        disabledTabs={disabledTabs}
                        disabledTabTooltips={disabledTabTooltips}
                        className="flex-wrap"
                      />
                      {canEditContent ? (
                        <Button
                          size="sm"
                          variant="cta"
                          data-testid="summary-save"
                          disabled={!isSummaryDirty || isSummarySaving}
                          onClick={() => void saveSummary()}
                        >
                          {isSummarySaving ? (
                            <>
                              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                              Saving
                            </>
                          ) : (
                            "Save"
                          )}
                        </Button>
                      ) : null}
                    </div>
                  </div>

                  {/* Right card — what was tested: one slide per tested
                      asset with a picture, opening the asset dialog (under
                      the headline on a phone); else the study cover, hidden
                      below md so the headline card gets the full width */}
                  {heroSlides.length > 0 ? (
                    <TestedAssetSlider
                      assets={assets}
                      slides={heroSlides}
                      itemTags={itemTags}
                      onOpen={setOpenAssetId}
                      className="w-full aspect-[16/10] md:aspect-auto md:w-1/3 shrink-0"
                    />
                  ) : coverImageUrl && (
                    <div className="hidden md:block w-1/3 shrink-0 rounded-2xl overflow-hidden bg-muted">
                      <FadeImage
                        src={coverImageUrl}
                        alt={studyName}
                        loading="eager"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>

                {/* 3-card row */}
                <SummaryTab
                  canonicalAnalysis={summaryAnalysis}
                  audienceProfiles={audienceProfiles}
                  audienceProfilesLoading={audienceProfilesLoading}
                  chatVisible={!isChatHidden}
                  onOpenChat={() => handleTabChange("chat")}
                  respondentCount={responses?.length}
                  onTwinChatClick={handleTwinChatClick}
                  survey={survey}
                  responses={responses}
                  analysisColumns={analysisColumns}
                  segmentationMappings={segmentationMappings}
                />

                {assets.length > 0 && (
                  <TestedAssetPreviewDialog
                    assets={assets}
                    itemTags={itemTags}
                    initialAssetId={openAssetId ?? undefined}
                    open={openAssetId !== null}
                    onOpenChange={(open) => {
                      if (!open) {
                        setOpenAssetId(null);
                      }
                    }}
                  />
                )}
              </ReportTabContent>
            </motion.div>
          )}

          {activeTab === "deck" && !isDeckHidden && (
            <motion.div
              key="deck-tab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex flex-col overflow-y-auto scrollbar-overlay"
            >
              <ReportTabContent>
                <DeckTab
                  deck={
                    canEditContent && deck
                      ? { ...deck, canEdit: true }
                      : deck
                  }
                  viewState={deckViewState}
                  slideOrder={slideOrder}
                  studyName={studyName}
                  studyId={studyId}
                  sampleDemographics={isPublicView ? null : canonicalAnalysis?.sampleDemographics ?? null}
                  chartSegmentationEnabled={!isPublicView}
                  shareToken={isPublicView ? shareToken : undefined}
                  onDeckUpdate={onDeckUpdate}
                  onLoadDeck={onLoadDeck}
                  canEdit={
                    canEditContent ? true : (deck?.canEdit ?? false)
                  }
                  isPublicView={isPublicView}
                  onRetryFailedExhibit={isPublicView ? undefined : onRegenerateSummary}
                  isRetryingFailedExhibit={isRegeneratingSummary}
                  focusSlideId={deckFocusSlideId}
                />
              </ReportTabContent>
            </motion.div>
          )}

          {activeTab === "chat" && !isChatHidden && (
            <motion.div
              key="chat-tab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              <ReportTabContent className="overflow-hidden">
                <ChatTab
                  audienceProfiles={audienceProfiles}
                  audienceProfilesLoading={audienceProfilesLoading}
                  populationName={audienceName || populationName}
                  responses={responses}
                  populationId={populationId}
                  experimentId={isPublicView ? undefined : studyId}
                  survey={survey}
                  canonicalAnalysis={canonicalAnalysis}
                  initialChatRespondentId={initialChatRespondentId}
                  onClearInitialTwin={() => setInitialChatRespondentId(undefined)}
                  shareToken={shareToken}
                  experimentLanguage={experimentLanguage}
                />
              </ReportTabContent>
            </motion.div>
          )}

          {activeTab === "analyst" && !isAnalystHidden && (
            <motion.div
              key="analyst-tab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              <ReportTabContent className="overflow-hidden">
                <AnalystTab
                  experimentId={studyId}
                  mode={analystMode}
                  storyline={storyline}
                  metricResults={canonicalAnalysis?.metricResults}
                  qualitativeResults={canonicalAnalysis?.qualitativeResults}
                  studyName={studyName}
                  slideNumbers={storylineSlideNumbers}
                  onOpenSlide={handleOpenStorylineSlide}
                />
              </ReportTabContent>
            </motion.div>
          )}

          {activeTab === "survey" && (
            <motion.div
              key="survey-tab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              <ReportTabContent className="overflow-hidden">
                <SurveyDataTab
                  studyId={isPublicView ? undefined : studyId}
                  shareToken={isPublicView ? shareToken : undefined}
                  survey={survey}
                  responses={responses}
                  populationAttributes={analysisColumns}
                  segmentationMappings={segmentationMappings}
                  constantContext={constantContext}
                  executiveSummary={executiveSummary}
                  canonicalAnalysis={canonicalAnalysis}
                  studyName={studyName}
                  createdAt={createdAt}
                  populationFilter={populationFilter}
                  hasScaleTables={hasScaleTables}
                  studyWorkbookStatus={studyWorkbookStatus}
                />
              </ReportTabContent>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </SurveyFiguresProvider>
    </ContentLanguageProvider>
  );
}
